import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import ReactDatePicker from 'react-datepicker';
import * as DateRangePickerUI from './DateRangePickerUI';
import { INTERNAL_DATE_FORMAT } from '../../../../config/constants';
import { IHighlightDates } from '../types';
import {
  convertDisplayDateFormatToInternalDateFormat,
  toCanonicalDateRange,
  toJavaScriptDate,
} from '../utils';
import DateRangeCustomHeader from './DateRangeCustomHeader';
import { IDatePickerFieldsFragment } from '../../../../utils/apollo/graphqlSchema';
import { IDateInput } from '../../../../redux/slices/oldBuilder/date/types';

interface IProps {
  dateInput: IDateInput;
  onChange: (newDatePicker: IDatePickerFieldsFragment) => void;
}

export type IDateRangePickerHandle = {
  reset: () => void;
};

/**
 * The ticket was KB-6195, and the bug was: Updating dates outside of <DatePickerWidget /> component will not trigger
 * the change of `monthDate` on the header.
 * Tried to use dates as the dependencies, but it doesn't trigger the useEffect if dates not changed,
 * for example:  selected 2023/01/01, and click multiple times of left arrow, then use shortcut to select 2023/01/01
 *               will not redirect the calendar back to 2023/01/01 since the date is same.
 * The solution is to remount the DatePicker when a date changes and expose a `reset()` handle to
 * trigger the reset from the outer components. The useRemountEffect hook encapsulates the remount and key genearation
 * login
 */
const useRemountEffect = (input: unknown) => {
  const [remountKey, setRemountKey] = useState(0);

  // trigger a remount by changing the key
  const remount = useCallback(() => {
    setRemountKey((s) => s + 1);
  }, []);

  // trigger remount when input changes
  useEffect(() => {
    remount();
  }, [input, remount]);

  return [remountKey, remount] as const;
};

export default forwardRef<IDateRangePickerHandle, IProps>(
  ({ dateInput, onChange }, ref) => {
    const { startDate, endDate, datePicker } = dateInput;
    const [startCalendarKey, remountStartCalendar] =
      useRemountEffect(startDate);
    const [endCalendarKey, remountEndCalendar] = useRemountEffect(endDate);

    const reset = useCallback(() => {
      remountStartCalendar();
      remountEndCalendar();
    }, [remountEndCalendar, remountStartCalendar]);

    useImperativeHandle(
      ref,
      () => ({
        reset,
      }),
      [reset],
    );

    const handleSelectStartDate = (date: Date | null) => {
      if (!date) {
        return;
      }

      const internalStartDate =
        convertDisplayDateFormatToInternalDateFormat(date);
      // if order is incorrect, swap start and end
      const { revisedStart, revisedEnd } = toCanonicalDateRange(
        internalStartDate,
        endDate,
        'start',
      );
      if (datePicker?.__typename === 'DatePickerSince') {
        onChange({
          __typename: 'DatePickerSince',
          start: revisedStart,
        });
      } else {
        onChange({
          __typename: 'DatePickerFixed',
          start: revisedStart,
          end: revisedEnd,
        });
      }
    };

    const handleSelectEndDate = (date: Date | null) => {
      if (!date) {
        return;
      }

      const internalEndDate =
        convertDisplayDateFormatToInternalDateFormat(date);
      // if order is incorrect, swap start and end
      const { revisedStart, revisedEnd } = toCanonicalDateRange(
        startDate,
        internalEndDate,
        'end',
      );
      onChange({
        __typename: 'DatePickerFixed',
        start: revisedStart,
        end: revisedEnd,
      });
    };

    /**
     * Do not select the start/end dates in both calendars
     */
    const getSpecialDateStyles = (
      styledDate: string,
    ): Array<IHighlightDates | Date> | undefined => {
      const res = [];

      if (startDate !== endDate) {
        res.push({
          [DateRangePickerUI.IGNORE_SELECTED_DATE_CLASS_NAME]: [
            toJavaScriptDate(styledDate),
          ],
        });
      }

      return res.length > 0 ? res : undefined;
    };

    const jsStartDate = toJavaScriptDate(startDate);
    const jsEndDate = toJavaScriptDate(endDate);

    return (
      <DateRangePickerUI.Container>
        <ReactDatePicker
          key={`start-cal-${startCalendarKey}`}
          inline
          selected={jsStartDate}
          startDate={jsStartDate}
          endDate={jsEndDate}
          dateFormat={INTERNAL_DATE_FORMAT}
          onChange={handleSelectStartDate}
          disabledKeyboardNavigation={true}
          formatWeekDay={(weekdayStr) => weekdayStr.charAt(0)}
          highlightDates={getSpecialDateStyles(endDate)}
          focusSelectedMonth
          renderCustomHeader={DateRangeCustomHeader}
        />
        <ReactDatePicker
          key={`end-cal-${endCalendarKey}`}
          inline
          selected={jsEndDate}
          startDate={jsStartDate}
          endDate={jsEndDate}
          dateFormat={INTERNAL_DATE_FORMAT}
          onChange={handleSelectEndDate}
          disabledKeyboardNavigation={true}
          formatWeekDay={(weekdayStr) => weekdayStr.charAt(0)}
          highlightDates={getSpecialDateStyles(startDate)}
          renderCustomHeader={DateRangeCustomHeader}
        />
      </DateRangePickerUI.Container>
    );
  },
);
